import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Settings, Search, AlertCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface RedditEmbedProps {
  subreddit?: string;
  username?: string;
  postUrl?: string;
}

export const RedditEmbed: React.FC<RedditEmbedProps> = ({ 
  subreddit = 'popular',
  username,
  postUrl 
}) => {
  const navigate = useNavigate();
  const embedRef = useRef<HTMLDivElement>(null);
  const [currentSubreddit, setCurrentSubreddit] = useState(subreddit);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  console.log('🤖 RedditEmbed: Setting up for', username ? `user: ${username}` : `subreddit: ${currentSubreddit}`);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    
    // Add a timeout to ensure we don't show loading indefinitely
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.log('🤖 RedditEmbed: Load timeout, assuming success or showing fallback');
        setIsLoading(false);
      }
    }, 8000);

    renderRedditEmbed();

    return () => clearTimeout(timeoutId);
  }, [currentSubreddit, username, postUrl]);

  const handleSubredditChange = () => {
    if (inputValue.trim()) {
      const newSubreddit = inputValue.trim().replace(/^r\//, '');
      console.log('🤖 RedditEmbed: Changing subreddit to:', newSubreddit);
      setCurrentSubreddit(newSubreddit);
      setInputValue('');
    }
  };

  const renderRedditEmbed = () => {
    if (!embedRef.current) return;

    // Clear existing content
    embedRef.current.innerHTML = '';

    let embedUrl: string;
    
    if (postUrl) {
      // If we have a specific post URL, use Reddit's embed format
      embedUrl = `${postUrl.replace('www.reddit.com', 'www.redditmedia.com')}/embed?ref_source=embed&ref=share&embed=true&theme=light`;
    } else if (username) {
      // For user profiles, show their posts
      embedUrl = `https://www.reddit.com/user/${username}/.embed?theme=light`;
    } else {
      // For subreddits, embed the subreddit feed
      embedUrl = `https://www.reddit.com/r/${currentSubreddit}/.embed?theme=light`;
    }

    console.log('🤖 RedditEmbed: Using embed URL:', embedUrl);

    // Create iframe element with better error handling
    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.width = '100%';
    iframe.height = '400';
    iframe.frameBorder = '0';
    iframe.scrolling = 'auto';
    iframe.sandbox = 'allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '0 0 8px 8px';
    iframe.style.minHeight = '400px';
    iframe.title = `Reddit ${username ? 'user' : 'subreddit'} feed`;
    iframe.loading = 'lazy';

    let hasLoaded = false;

    iframe.onload = () => {
      console.log('🤖 RedditEmbed: Iframe loaded successfully');
      hasLoaded = true;
      setIsLoading(false);
    };

    iframe.onerror = () => {
      console.error('🤖 RedditEmbed: Iframe failed to load');
      setHasError(true);
      setIsLoading(false);
    };

    // Backup timeout in case onload doesn't fire
    setTimeout(() => {
      if (!hasLoaded) {
        console.log('🤖 RedditEmbed: Backup timeout - assuming loaded');
        setIsLoading(false);
      }
    }, 6000);

    embedRef.current.appendChild(iframe);
  };

  const renderFallbackContent = () => {
    const fallbackUrl = username 
      ? `https://www.reddit.com/user/${username}`
      : `https://www.reddit.com/r/${currentSubreddit}`;

    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <AlertCircle className="h-12 w-12 text-orange-500 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Reddit Content Unavailable</h3>
        <p className="text-gray-600 mb-4">
          Unable to load Reddit content. This may be due to privacy settings or restrictions.
        </p>
        <Button 
          variant="outline" 
          onClick={() => window.open(fallbackUrl, '_blank')}
          className="mb-2"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          View on Reddit
        </Button>
        <p className="text-xs text-gray-500">
          Try viewing the content directly on Reddit
        </p>
      </div>
    );
  };

  return (
    <Card className="h-[500px]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">r</span>
            </div>
            Reddit {username ? `(u/${username})` : `(r/${currentSubreddit})`}
          </CardTitle>
          <Button 
            onClick={() => navigate('/dashboard-sidebar/platforms')}
            variant="ghost" 
            size="sm"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
        
        {!username && !postUrl && (
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="Enter subreddit name..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubredditChange()}
              className="text-sm"
            />
            <Button onClick={handleSubredditChange} size="sm" variant="outline">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="p-0 relative">
        <div className="w-full h-[400px] relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading Reddit content...</p>
              </div>
            </div>
          )}
          
          {hasError ? (
            renderFallbackContent()
          ) : (
            <div 
              ref={embedRef}
              className="w-full h-full"
              style={{ minHeight: '400px' }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};